import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Play, Pause, RotateCcw, Snowflake, Wind, Heart, Brain, AlertTriangle, Shield, Users, Timer } from 'lucide-react';
import { ConditionSpecificTools } from './ConditionSpecificTools';

interface CopingToolsLibraryProps {
  user: any;
}

export const CopingToolsLibrary = ({ user }: CopingToolsLibraryProps) => {
  const [activeExercise, setActiveExercise] = useState<string | null>(null);
  const [selectedCondition, setSelectedCondition] = useState<string | null>(null);
  const [breathingTimer, setBreathingTimer] = useState(0);
  const [breathingPhase, setBreathingPhase] = useState<'inhale' | 'hold' | 'exhale'>('inhale');
  const [groundingStep, setGroundingStep] = useState(0);

  const conditionCategories = [
    {
      id: 'ptsd',
      title: 'PTSD & Trauma',
      description: 'Flashbacks, dissociation, hypervigilance',
      icon: <Shield className="h-5 w-5" />,
      color: 'border-primary/30 hover:bg-primary/10'
    },
    {
      id: 'bpd',
      title: 'Emotional Overwhelm',
      description: 'Intense emotions, BPD, mood swings',
      icon: <Heart className="h-5 w-5" />,
      color: 'border-secondary/30 hover:bg-secondary/10'
    },
    {
      id: 'ocd',
      title: 'OCD & Compulsions',
      description: 'Obsessive thoughts, ritual behaviours',
      icon: <Timer className="h-5 w-5" />,
      color: 'border-soft-accent/30 hover:bg-soft-accent/10'
    },
    {
      id: 'selfharm',
      title: 'Self-Harm Urges',
      description: 'Safe alternatives to self-injury',
      icon: <AlertTriangle className="h-5 w-5" />,
      color: 'border-primary/30 hover:bg-primary/10'
    }
  ];

  const generalTools = [
    {
      id: 'grounding-54321',
      title: '5-4-3-2-1 Grounding',
      description: 'Use your senses to ground yourself in the present moment',
      icon: <Brain className="h-5 w-5" />,
      category: 'Grounding',
      duration: '3-5 minutes'
    },
    {
      id: 'breathing-478',
      title: '4-7-8 Breathing',
      description: 'Calm your nervous system with controlled breathing',
      icon: <Wind className="h-5 w-5" />,
      category: 'Breathing',
      duration: '2-4 minutes'
    },
    {
      id: 'cold-therapy',
      title: 'Cold Water Technique',
      description: 'Reset your nervous system with cold stimulation',
      icon: <Snowflake className="h-5 w-5" />,
      category: 'Physical',
      duration: '1-2 minutes'
    },
    {
      id: 'urge-surfing',
      title: 'Urge Surfing',
      description: 'Ride out difficult urges and emotions',
      icon: <Heart className="h-5 w-5" />,
      category: 'Mindfulness',
      duration: '3-5 minutes'
    }
  ];

  const startGrounding54321 = () => {
    setActiveExercise('grounding-54321');
    setGroundingStep(0);
  };

  const nextGroundingStep = () => {
    setGroundingStep(prev => prev + 1);
  };

  const resetExercise = () => {
    setActiveExercise(null);
    setSelectedCondition(null);
    setBreathingTimer(0);
    setBreathingPhase('inhale');
    setGroundingStep(0);
  };

  const groundingSteps = [
    { sense: 'See', count: 5, instruction: 'Look around and name 5 things you can see', color: 'bg-primary/10 text-primary' },
    { sense: 'Touch', count: 4, instruction: 'Notice 4 things you can touch or feel', color: 'bg-primary/10 text-primary' },
    { sense: 'Hear', count: 3, instruction: 'Listen for 3 things you can hear', color: 'bg-primary/10 text-primary' },
    { sense: 'Smell', count: 2, instruction: 'Identify 2 things you can smell', color: 'bg-primary/10 text-primary' },
    { sense: 'Taste', count: 1, instruction: 'Notice 1 thing you can taste', color: 'bg-primary/10 text-primary' }
  ];

  // Handle condition-specific tools
  if (selectedCondition && ['ptsd', 'bpd', 'ocd', 'selfharm'].includes(selectedCondition)) {
    return (
      <ConditionSpecificTools 
        user={user} 
        condition={selectedCondition as 'ptsd' | 'bpd' | 'ocd' | 'selfharm'} 
        onBack={() => setSelectedCondition(null)} 
      />
    );
  }

  if (activeExercise === 'grounding-54321') {
    const currentStep = groundingSteps[groundingStep];
    const progress = (groundingStep / groundingSteps.length) * 100;

    return (
      <div className="space-y-6">
        <Card className="border-primary/20 bg-gradient-therapeutic">
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle className="text-primary">5-4-3-2-1 Grounding Exercise</CardTitle>
              <Button onClick={resetExercise} variant="outline" size="sm">
                <RotateCcw className="h-4 w-4 mr-2" />
                Reset
              </Button>
            </div>
            <Progress value={progress} className="mt-2" />
          </CardHeader>
          <CardContent className="space-y-6">
            {groundingStep < groundingSteps.length ? (
              <div className="text-center space-y-4">
                <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full text-2xl font-bold ${currentStep.color}`}>
                  {currentStep.count}
                </div>
                <h3 className="text-xl font-semibold">{currentStep.sense}</h3>
                <p className="text-lg text-muted-foreground">{currentStep.instruction}</p>
                <div className="space-y-2 mt-6">
                  <p className="text-sm text-muted-foreground">Take your time. When you're ready, click next.</p>
                  <Button onClick={nextGroundingStep} size="lg" className="w-full">
                    {groundingStep === groundingSteps.length - 1 ? 'Complete' : 'Next'}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center space-y-4">
                <div className="text-primary text-6xl">✓</div>
                <h3 className="text-xl font-semibold text-primary">Exercise Complete</h3>
                <p className="text-muted-foreground">
                  Great job! You've successfully grounded yourself in the present moment.
                </p>
                <Button onClick={resetExercise} className="bg-primary hover:bg-primary/90">
                  Return to Tools
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (activeExercise === 'breathing-478') {
    return (
      <BreathingExercise onReset={resetExercise} />
    );
  }

  if (activeExercise === 'cold-therapy') {
    return (
      <ColdTherapyGuide onReset={resetExercise} />
    );
  }

  if (activeExercise === 'urge-surfing') {
    return (
      <UrgeSurfingGuide onReset={resetExercise} />
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-primary">Coping Tools Library</CardTitle>
          <p className="text-muted-foreground">
            Evidence-based techniques organised by condition and symptom type.
          </p>
        </CardHeader>
      </Card>

      {/* Condition-Specific Categories */}
      <Card className="bg-primary/5 border-primary/20">
        <CardHeader>
          <CardTitle className="text-primary flex items-center gap-2">
            <Users className="h-5 w-5" />
            Condition-Specific Tools
          </CardTitle>
          <p className="text-muted-foreground text-sm">
            Specialised techniques developed for specific mental health conditions
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-4">
            {conditionCategories.map((category) => (
              <Card 
                key={category.id} 
                className={`cursor-pointer transition-all ${category.color}`}
                onClick={() => setSelectedCondition(category.id)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="text-primary">{category.icon}</div>
                    <div>
                      <h3 className="font-semibold">{category.title}</h3>
                      <p className="text-muted-foreground text-sm">{category.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* General Tools */}
      <Card>
        <CardHeader>
          <CardTitle className="text-primary">General Coping Tools</CardTitle>
          <p className="text-muted-foreground text-sm">
            Universal techniques helpful for anyone in distress
          </p>
        </CardHeader>
      </Card>

      <div className="grid gap-4">
        {generalTools.map((tool) => (
          <Card key={tool.id} className="cursor-pointer hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-start gap-3">
                  <div className="text-primary mt-1">{tool.icon}</div>
                  <div>
                    <h3 className="font-semibold">{tool.title}</h3>
                    <p className="text-muted-foreground text-sm">{tool.description}</p>
                    <div className="flex gap-2 mt-2">
                      <Badge variant="outline">{tool.category}</Badge>
                      <Badge variant="secondary">{tool.duration}</Badge>
                    </div>
                  </div>
                </div>
                <Button
                  onClick={() => setActiveExercise(tool.id)}
                  className="bg-primary hover:bg-primary/90"
                >
                  <Play className="h-4 w-4 mr-2" />
                  Start
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Emergency Micro-Tasks */}
      <Card className="border-secondary/20 bg-secondary/10">
        <CardHeader>
          <CardTitle className="text-primary">Quick Micro-Tasks</CardTitle>
          <p className="text-foreground text-sm">
            Tiny, achievable activities to improve mood right now
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid gap-2">
            {[
              'Take 3 deep breaths',
              'Drink a glass of water',
              'Text one person you care about',
              'Step outside for 2 minutes',
              'Listen to one favourite song',
              'Write down one thing you\'re grateful for',
              'Do 10 jumping jacks or stretch',
              'Make yourself a warm drink'
            ].map((task, index) => (
              <Button
                key={index}
                variant="outline"
                className="text-left justify-start border-secondary hover:bg-secondary/10"
                onClick={() => {
                  // Mark as done (could track in state if needed)
                  const button = document.getElementById(`task-${index}`);
                  if (button) {
                    button.classList.add('bg-primary/10', 'border-primary/30', 'text-primary');
                    button.textContent = '✓ ' + task; // Fixed: Use textContent instead of innerHTML to prevent XSS
                  }
                }}
                id={`task-${index}`}
              >
                {task}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const BreathingExercise = ({ onReset }: { onReset: () => void }) => {
  const [phase, setPhase] = useState<'inhale' | 'hold' | 'exhale'>('inhale');
  const [count, setCount] = useState(4);
  const [cycle, setCycle] = useState(1);
  const [isActive, setIsActive] = useState(false);

  const startBreathing = () => {
    setIsActive(true);
    // Implementation would include timer logic
  };

  return (
    <Card className="border-secondary/20 bg-secondary/10">
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle className="text-primary">4-7-8 Breathing Exercise</CardTitle>
          <Button onClick={onReset} variant="outline" size="sm">
            <RotateCcw className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>
      </CardHeader>
      <CardContent className="text-center space-y-6">
        <div className="text-6xl font-light text-primary">
          {phase === 'inhale' && '↑ Inhale'}
          {phase === 'hold' && '⊙ Hold'}
          {phase === 'exhale' && '↓ Exhale'}
        </div>
        
        <div className="text-4xl font-mono text-primary">{count}</div>
        
        <div className="space-y-2">
          <p className="text-muted-foreground">
            Cycle {cycle} of 4 • {phase.charAt(0).toUpperCase() + phase.slice(1)} for {
              phase === 'inhale' ? 4 : phase === 'hold' ? 7 : 8
            } counts
          </p>
        </div>

        <Button 
          onClick={startBreathing} 
          size="lg" 
          className="bg-secondary hover:bg-secondary/90"
          disabled={isActive}
        >
          {isActive ? <Pause className="h-4 w-4 mr-2" /> : <Play className="h-4 w-4 mr-2" />}
          {isActive ? 'Breathing...' : 'Start Breathing'}
        </Button>

        <div className="text-sm text-muted-foreground max-w-md mx-auto">
          <p className="mb-2"><strong>Instructions:</strong></p>
          <p>• Inhale through nose for 4 counts</p>
          <p>• Hold breath for 7 counts</p>
          <p>• Exhale through mouth for 8 counts</p>
          <p>• Repeat 4 cycles</p>
        </div>
      </CardContent>
    </Card>
  );
};

const ColdTherapyGuide = ({ onReset }: { onReset: () => void }) => {
  return (
    <Card className="border-soft-accent/20 bg-soft-accent/10">
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle className="text-soft-accent">Cold Water Technique</CardTitle>
          <Button onClick={onReset} variant="outline" size="sm">
            <RotateCcw className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="text-center text-6xl text-soft-accent mb-4">
          <Snowflake />
        </div>
        
        <div className="space-y-4">
          <div className="bg-background p-4 rounded-lg border border-soft-accent/20">
            <h3 className="font-semibold text-soft-accent mb-2">Step 1: Get Cold Water</h3>
            <p className="text-muted-foreground">Fill a bowl with cold water or use cold tap water. Add ice if available.</p>
          </div>
          
          <div className="bg-background p-4 rounded-lg border border-soft-accent/20">
            <h3 className="font-semibold text-soft-accent mb-2">Step 2: Apply to Face</h3>
            <p className="text-muted-foreground">Splash cold water on your face, focusing on temples and around eyes. Or hold a cold, damp cloth to your face.</p>
          </div>
          
          <div className="bg-background p-4 rounded-lg border border-soft-accent/20">
            <h3 className="font-semibold text-soft-accent mb-2">Step 3: Hold Breath</h3>
            <p className="text-muted-foreground">While applying cold, hold your breath for 30 seconds if possible.</p>
          </div>
          
          <div className="bg-background p-4 rounded-lg border border-soft-accent/20">
            <h3 className="font-semibold text-soft-accent mb-2">Alternative: Ice Cubes</h3>
            <p className="text-muted-foreground">Hold ice cubes in your hands or press against temples for 30-60 seconds.</p>
          </div>
        </div>

        <div className="bg-soft-accent/10 p-4 rounded-lg">
          <p className="text-soft-accent text-sm">
            <strong>Why this works:</strong> Cold activates your vagus nerve, which quickly calms your nervous system and reduces intense emotions.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

const UrgeSurfingGuide = ({ onReset }: { onReset: () => void }) => {
  return (
    <Card className="border-primary/20 bg-gradient-therapeutic">
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle className="text-primary">Urge Surfing</CardTitle>
          <Button onClick={onReset} variant="outline" size="sm">
            <RotateCcw className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="text-center text-6xl text-primary mb-4">🌊</div>
        
        <div className="bg-primary/10 p-4 rounded-lg mb-4">
          <p className="text-primary text-center font-medium">
            "The urge will rise and fall like a wave. Breathe in for 4, out for 6. Name the urge 'urge' and watch it pass. You are not the urge."
          </p>
        </div>

        <div className="space-y-4">
          <div className="bg-background p-4 rounded-lg border border-primary/20">
            <h3 className="font-semibold text-primary mb-2">1. Notice the Urge</h3>
            <p className="text-muted-foreground">Acknowledge: "I'm having an urge to..." Don't judge it, just notice it.</p>
          </div>
          
          <div className="bg-background p-4 rounded-lg border border-primary/20">
            <h3 className="font-semibold text-primary mb-2">2. Breathe Through It</h3>
            <p className="text-muted-foreground">Inhale for 4 counts, exhale for 6 counts. Focus on your breath, not the urge.</p>
          </div>
          
          <div className="bg-background p-4 rounded-lg border border-primary/20">
            <h3 className="font-semibold text-primary mb-2">3. Observe Like a Wave</h3>
            <p className="text-muted-foreground">Urges peak and then naturally decrease. You don't have to act on them.</p>
          </div>
          
          <div className="bg-background p-4 rounded-lg border border-primary/20">
            <h3 className="font-semibold text-primary mb-2">4. Wait It Out</h3>
            <p className="text-muted-foreground">Most urges pass within 20-30 minutes if you don't feed them with actions.</p>
          </div>
        </div>

        <div className="bg-background p-4 rounded-lg border-l-4 border-l-primary">
          <p className="text-sm text-muted-foreground">
            <strong>Remember:</strong> You are not your urges. They are temporary visitors in your mind. You can observe them without becoming them.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};